import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import Layout from "@/components/Layout";
import HeroSub from "@/components/sections/HeroSub";
import SectionRenderer from "@/components/sections/SectionRenderer";
import CtaFinal from "@/components/sections/CtaFinal";
import { safeFetch } from "@/lib/sanity";
import { subPageBySlugQuery } from "@/lib/queries";
import { subPagesFallback } from "@/lib/fallback";
import type { SubPage as SubPageType } from "@/lib/types";

interface Props {
  /** If provided, this slug is used instead of the :slug route param. Used for
   *  routes like /about and /contact where the URL is not parameterized. */
  slug?: string;
}

export default function SubPage({ slug: slugProp }: Props) {
  const params = useParams<{ slug: string }>();
  const slug = slugProp ?? params.slug ?? "";

  const fallback = subPagesFallback[slug];
  const [data, setData] = useState<SubPageType | undefined>(fallback);

  useEffect(() => {
    if (!slug) return;
    let active = true;
    setData(subPagesFallback[slug]); // reset fallback on slug change
    safeFetch<SubPageType>(subPageBySlugQuery, { slug }).then((d) => {
      if (active && d) setData(d);
    });
    return () => {
      active = false;
    };
  }, [slug]);

  if (!data) return <Navigate to="/" replace />;

  return (
    <Layout seo={data.seo}>
      <HeroSub data={data.hero} />
      {data.sections.map((section, i) => (
        <SectionRenderer key={i} section={section} />
      ))}
      <CtaFinal data={data.ctaFinal} />
    </Layout>
  );
}
